import React, { useState, useEffect, useCallback } from 'react';
import { 
  X,  Send, Upload, 
  MessageCircle, Check, Lightbulb, Bug, Shield, Zap, HelpCircle, ArrowRight,
  UserPlus, Users, UserMinus, Cog, Key, Workflow
} from 'lucide-react';
import { createTicket } from '../../../../models/helpdeskModel'; 
import { getDepartmentUsers, getAllUsers, getHelpdeskDepartments, getHelpdeskRoles, getDivisionDepartments } from '../../../../models/helpdeskModel';
import DatePicker from '../../../../components/common/CustomDatePicker';
import { getCategories, getSystems } from '../../../../models/helpdeskModel';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../context/AuthContext';
import { buildTicketPayload } from '../../../../utils/helpdeskPayloadMappers';

// Import brand colors
import '../../../../styles/colors.css';

// Import common components
import Button from '../../../../components/common/Button'; 
import MultiFileInput from '../../../../components/common/MultiFileInput';
import Select from '../../../../components/common/Select';
import SearchableSelect from '../../../../components/common/SearchableSelect';

const NewTicketModal = ({ isOpen, onClose, onTicketCreated, currentUser }) => {
  const { user, isStaff, can } = useAuth();
  
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: '', // Will be set when categories load
    system_id: '',
    type: '' // Add type field to form data
  });

  const [systems, setSystems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  // const [departments, setDepartments] = useState([]); // removed duplicate
  const [attachments, setAttachments] = useState([]);
  const [pastedImages, setPastedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [edamsBilling, setEdamsBilling] = useState(false);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [people, setPeople] = useState([]);
  const [divisionDepartments, setDivisionDepartments] = useState([]);

  // All ticket types as defined in HELPDESK_TICKET_FLOWS_GUIDE.md
  const allTicketTypes = [
    { 
      value: 'support', 
      label: 'General Support', 
      icon: HelpCircle, 
      description: 'General technical support and IT assistance',
      color: 'bg-blue-50 text-blue-700 border-blue-200',
      prefix: 'SUP',
      requiresApproval: false,
      isWorkflow: false
    }, 
    { 
      value: 'change_request', 
      label: 'Enhancement Request', 
      icon: Lightbulb, 
      description: 'New features, system improvements, hardware requests',
      color: 'bg-green-50 text-green-700 border-green-200',
      prefix: 'ENH',
      requiresApproval: true,
      isWorkflow: true
    }, 
    { 
      value: 'defect', 
      label: 'Defect Report', 
      icon: Bug, 
      description: 'Broken hardware, unexpected behavior, or malfunctioning features',
      color: 'bg-red-50 text-red-700 border-red-200',
      prefix: 'DEF',
      requiresApproval: false,
      isWorkflow: false
    },
    { 
      value: 'new_user', 
      label: 'New User Setup', 
      icon: UserPlus, 
      description: 'Creating new user accounts and system access',
      color: 'bg-purple-50 text-purple-700 border-purple-200',
      prefix: 'NEW',
      requiresApproval: true,
      requiredPermission: 'Create New User',
      isWorkflow: true
    },
    { 
      value: 'staff_movement', 
      label: 'Staff Movement', 
      icon: Users, 
      description: 'Department transfers, role changes, access updates',
      color: 'bg-orange-50 text-orange-700 border-orange-200',
      prefix: 'SMV',
      requiresApproval: true,
      requiredPermission: 'Create Staff Movement',
      isWorkflow: true
    },
    { 
      value: 'access_modification', 
      label: 'Edams Access Modification', 
      icon: Cog, 
      description: 'Changing existing user limits and access rights',
      color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      prefix: 'AMD',
      requiresApproval: true,
      requiredPermission: 'Create Access Modification',
      isWorkflow: true
    },
    { 
      value: 'access_termination', 
      label: 'Access Termination', 
      icon: UserMinus, 
      description: 'Disabling accounts, removing access, staff departures',
      color: 'bg-gray-50 text-gray-700 border-gray-200',
      prefix: 'TERM',
      requiresApproval: true,
      requiredPermission: 'Create Access Termination',
      isWorkflow: true
    },
    { 
      value: 'password_reset', 
      label: 'Password Reset', 
      icon: Key, 
      description: 'Resetting a supplier\'s password in a specific system',
      color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      prefix: 'PWD',
      requiresApproval: true,
      requiredPermission: 'Create Password Reset',
      isWorkflow: true
    }
  ];

  // Filter ticket types based on user permissions
  const ticketTypes = allTicketTypes.filter(type => {
    // If the ticket type requires a specific permission
    if (type.requiredPermission) {
      // Show only if user has the required permission OR user ID is 1 (admin override)
      const isUserIdOne = user?.user_id === 1 || user?.user_id === '1';
      // Also show to supervisors, managers, and HODs
      const isSupervisor = user?.supervisor === 1 || user?.supervisor === '1';
      const isManager = user?.manager === 1 || user?.manager === '1';
      const isHOD = user?.hod === 1 || user?.hod === '1';
      
      return can(type.requiredPermission) || isUserIdOne || isSupervisor || isManager || isHOD;
    }
    // Show all other ticket types to everyone
    return true;
  });

  const priorities = [
    { 
      value: 'low', 
      label: 'Low', 
      description: 'Minor issue, no business impact',
      color: 'text-green-600 bg-green-100',
      badge: 'bg-green-500'
    },
    { 
      value: 'medium', 
      label: 'Medium', 
      description: 'Standard request, normal processing',
      color: 'text-blue-600 bg-blue-100',
      badge: 'bg-blue-500'
    },
    { 
      value: 'high', 
      label: 'High', 
      description: 'Important issue, affects productivity',
      color: 'text-orange-600 bg-orange-100',
      badge: 'bg-orange-500'
    },
    { 
      value: 'urgent', 
      label: 'Urgent', 
      description: 'Critical issue, immediate attention required',
      color: 'text-red-600 bg-red-100',
      badge: 'bg-red-500'
    }
  ];

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
      resetForm();
    }
  }, [isOpen]);

  // Listen for preset type from launcher
  useEffect(() => {
    const handler = (e) => {
      const presetType = e?.detail?.type;
      if (!presetType) return;
      setFormData(prev => ({ ...prev, type: presetType }));
      setCurrentStep(3);
    };
    window.addEventListener('helpdesk:setType', handler);
    return () => window.removeEventListener('helpdesk:setType', handler);
  }, []);


  // Auto-populate workflow fields from current user (mirrors PHP views' simplicity)
  useEffect(() => {
    if (currentStep !== 3) return;
    if (!['new_user','staff_movement','access_modification','access_termination'].includes(formData.type)) return;

    const fullName = user?.user_full_name || user?.name || '';
    const parts = fullName.trim().split(' ');
    const firstName = formData.first_name || parts[0] || '';
    const lastName = formData.last_name || (parts.length > 1 ? parts.slice(1).join(' ') : '');
    const department = formData.department || user?.department_name || user?.department || '';
    const position = formData.position || user?.role_name || user?.role || '';

    // Staff movement current info
    const currentDept = formData.current_department || department;
    const currentPos = formData.current_position || position;

    setFormData(prev => ({
      ...prev,
      // Only pre-populate first_name for staff_movement, not for new_user
      first_name: formData.type === 'staff_movement' ? firstName : prev.first_name,
      last_name: lastName,
      department,
      position,
      current_department: currentDept,
      current_position: currentPos
    }));
  }, [currentStep, formData.type, user]);

  // Load people list for manager view (used for Staff Movement user selection)
  useEffect(() => {
    const isWorkflow = ['staff_movement','access_modification','access_termination','new_user','change_request','password_reset'].includes(formData.type);
    if (!isOpen || currentStep !== 3 || !isWorkflow) return;
    (async () => {
      try {
        const resp = await getDepartmentUsers();
        const users = resp?.data?.users || resp?.users || [];
        const mapped = users.map(u => ({
          value: u.user_id,
          label: `${u.user_full_name}${u.employee_number ? ' - ' + u.employee_number : ''}`,
          department: u.department || u.department_name || '',
          department_id: u.department || u.department_id || '',
          position: u.position || u.role_name || '',
          role: u.role || u.role_id || '',
          role_id: u.role || u.role_id || ''
        }));
        setPeople(mapped);
        
        // Note: assigned_post will only be populated when a user is selected from the dropdown
      } catch (e) {
        console.error('Error loading department users:', e);
        setPeople([]);
      }
    })();
  }, [isOpen, currentStep, formData.type]);

  // Load departments and roles for Staff Movement fields
  useEffect(() => {
    if (!isOpen || currentStep !== 3 || formData.type !== 'staff_movement') return;
    (async () => {
      try {
        const [deptResp, rolesResp] = await Promise.all([
          getHelpdeskDepartments(),
          getHelpdeskRoles()
        ]);
        const deptList = deptResp?.departments || deptResp?.data?.departments || deptResp?.data || [];
        const roleList = rolesResp?.roles || rolesResp?.data?.roles || rolesResp?.data || [];
        setDepartments(deptList);
        setRoles(roleList);
        
        // Prefill division/role if available from user
        // Handle different possible field names from user object
        const userDept = user?.department || user?.department_id || user?.dept_id || '';
        const userRole = user?.role || user?.role_id || user?.user_role || '';
        
        setFormData(prev => ({
          ...prev,
          department: prev.department || userDept,
          // Don't auto-populate assigned_post - only populate when user is selected
          assigned_post: prev.assigned_post || ''
        }));
      } catch (e) {
        setDepartments([]);
        setRoles([]);
      }
    })();
  }, [isOpen, currentStep, formData.type, user]);

  // Load departments and roles for New User fields
  useEffect(() => {
    if (!isOpen || currentStep !== 3 || formData.type !== 'new_user') return;
    (async () => {
      try {
        const [deptResp, divDeptResp, rolesResp] = await Promise.all([
          getHelpdeskDepartments(),
          getDivisionDepartments(),
          getHelpdeskRoles()
        ]);
        const deptList = deptResp?.departments || deptResp?.data?.departments || deptResp?.data || [];
        const divDeptList = divDeptResp?.division_departments || divDeptResp?.data?.division_departments || divDeptResp?.data || [];
        const roleList = rolesResp?.roles || rolesResp?.data?.roles || rolesResp?.data || [];
        setDepartments(deptList);
        setDivisionDepartments(divDeptList);
        setRoles(roleList);
        
        // Prefill division and department from current user (but not assigned post)
        const userDept = user?.department || user?.department_id || user?.dept_id || '';
        
        setFormData(prev => ({
          ...prev,
          department: prev.department || userDept, // Pre-fill division field (departments dropdown)
          division: prev.division || userDept, // Pre-fill department field (divisionDepartments dropdown)
          // post field should remain empty - no pre-selection
        }));
      } catch (e) {
        setDepartments([]);
        setDivisionDepartments([]);
        setRoles([]);
      }
    })();
  }, [isOpen, currentStep, formData.type, user]);

  const isManagerRole = React.useMemo(() => {
    const role = (localStorage.getItem('user_role') || '').toLowerCase();
    return role.includes('manager') || role.includes('supervisor') || role.includes('hod') || role.includes('director');
  }, []);

  // Load ALL users and departments for Access Termination (HR function - needs all users)
  useEffect(() => {
    if (!isOpen || currentStep !== 3 || formData.type !== 'access_termination') return;
    (async () => {
      try {
        const [usersResp, deptResp] = await Promise.all([
          getAllUsers(), // Get ALL users, not just department users
          getHelpdeskDepartments()
        ]);
        
        const users = usersResp?.data?.users || usersResp?.users || [];
        const mapped = users.map(u => ({
          value: u.user_id,
          label: `${u.user_full_name}${u.employee_number ? ' - ' + u.employee_number : ''}`,
          department: u.department || u.department_name || '',
          department_id: u.department || u.department_id || '',
          position: u.position || u.role_name || '',
          role: u.role || u.role_id || '',
          role_id: u.role || u.role_id || ''
        }));
        setPeople(mapped);
        
        const deptList = deptResp?.departments || deptResp?.data?.departments || deptResp?.data || [];
        setDepartments(deptList);
        
        // Note: User selection is now REQUIRED for access termination
        // No auto-population - user must explicitly select from dropdown
      } catch (e) {
        console.error('Error loading users for access termination:', e);
        setPeople([]);
        setDepartments([]);
      }
    })();
  }, [isOpen, currentStep, formData.type]);

  // Load people, departments and roles for Access Modification
  useEffect(() => {
    if (!isOpen || currentStep !== 3 || formData.type !== 'access_modification') return;
    (async () => {
      try {
        const [usersResp, deptResp, rolesResp] = await Promise.all([
          getDepartmentUsers(),
          getHelpdeskDepartments(),
          getHelpdeskRoles()
        ]);
        
        const users = usersResp?.data?.users || usersResp?.users || [];
        const mapped = users.map(u => ({
          value: u.user_id,
          label: `${u.user_full_name}${u.employee_number ? ' - ' + u.employee_number : ''}`,
          department: u.department || u.department_name || '',
          department_id: u.department || u.department_id || '',
          position: u.position || u.role_name || '',
          role: u.role || u.role_id || '',
          role_id: u.role || u.role_id || ''
        }));
        setPeople(mapped);
        
        const deptList = deptResp?.departments || deptResp?.data?.departments || deptResp?.data || [];
        const roleList = rolesResp?.roles || rolesResp?.data?.roles || rolesResp?.data || [];
        setDepartments(deptList);
        setRoles(roleList);
        
        // Auto-populate for non-managers (self-modification) - but not first name
        if (!isManagerRole) {
          const fullName = user?.user_full_name || '';
          const nameParts = fullName.split(' ');
          const lastName = nameParts.slice(1).join(' ') || '';
          const userDept = user?.department || user?.department_id || user?.dept_id || '';
          const userRole = user?.role || user?.role_id || user?.user_role || '';
          
          setFormData(prev => ({
            ...prev,
            last_name: lastName,
            department: prev.department || userDept,
            current_post: prev.current_post || userRole,
            edams_modification: 'modification'
          }));
        }
      } catch (e) {
        setPeople([]);
        setDepartments([]);
        setRoles([]);
      }
    })();
  }, [isOpen, currentStep, formData.type, user, isManagerRole]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [systemsResponse, categoriesResponse] = await Promise.all([
        getSystems(),
        getCategories()
      ]);
 

      if (systemsResponse.success || systemsResponse.status === 1) {
        // Extract the systems from the nested data structure
        const systemsData = systemsResponse.data?.data?.rows || systemsResponse.data || [];
 
        // Filter out systems with empty names and add fallback names
        const validSystems = systemsData.filter(system => {
          const hasName = system.asset_name && system.asset_name.trim() !== ''; 
          return hasName;
        }); 
        setSystems(validSystems);
      } else {

      }

      if (categoriesResponse.success || categoriesResponse.status === 1) {
        const data = categoriesResponse.data;
        // Handle nested data structure: data.data.categories or data.categories or data
        const apiCategories = data?.data?.categories || data?.categories || data || [];
        
        // Transform categories to have value and label properties for the dropdown
        const formattedCategories = apiCategories.map(category => ({
          value: category.category_id || category.value,
          label: category.category_name || category.label || category.name,
          ...category
        }));
        
        setCategories(formattedCategories);
        
        
        // For users and departments, we'll need to get these from the helpdesk API
        setUsers([]);
        setDepartments([]);
      } else {
        console.warn('Failed to load categories from API, using fallback');
        // Fallback categories if API fails
        const fallbackCategories = [
          { value: 'general', label: 'General Support' },
          { value: 'technical', label: 'Technical Issue' },
          { value: 'access', label: 'Access Request' }
        ];
        toast.warn('Using default categories - please contact IT to configure category system');
      }
    } catch (error) {
      console.error('Error loading form data:', error);
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    // Debug logging to help identify object values
    if (typeof value === 'object' && value !== null) {
      console.error('Object value detected in form input:', name, value);
    }
    // Ensure value is always a string to prevent object rendering errors
    const stringValue = typeof value === 'object' ? '' : String(value || '');
    setFormData(prev => ({
      ...prev,
      [name]: stringValue
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [errors]);

  const handleTypeSelect = (type) => {
    const selectedType = allTicketTypes.find(t => t.value === type);
    const isWorkflowType = selectedType?.isWorkflow;
    
    setFormData(prev => ({ 
      ...prev, 
      type,
      // Auto-fill subject with type name for workflow types
      subject: isWorkflowType ? selectedType.label : prev.subject
    }));
    setErrors(prev => ({ ...prev, type: null }));
    // Automatically advance to step 2 after selecting ticket type
    setCurrentStep(2);
  };

  const handleAttachmentsChange = (files) => {
    setAttachments(files);
  };

  const handlePaste = (e) => {
    const clipboardData = e.clipboardData;
    const items = clipboardData.items;
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        const reader = new FileReader();
        
        reader.onload = (event) => {
          const imageData = {
            file: file,
            preview: event.target.result,
            name: `pasted-image-${Date.now()}.png`
          };
          setPastedImages(prev => [...prev, imageData]);
        };
        
        reader.readAsDataURL(file);
        break;
      }
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.indexOf('image') !== -1) {
        const reader = new FileReader();
        
        reader.onload = (event) => {
          const imageData = {
            file: file,
            preview: event.target.result,
            name: file.name
          };
          setPastedImages(prev => [...prev, imageData]);
        };
        
        reader.readAsDataURL(file);
      }
    }
    e.target.value = ''; // Reset file input
  };

  const removeImage = (index) => {
    setPastedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    
    // Prevent double submission
    if (submitLoading) { 
      return;
    }
    
    // Only validate for final submission (Step 3)
    if (currentStep !== 3) { 
      return;
    }
    
    const newErrors = {};

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    // Description is optional since users often just paste screenshots
    // if (!formData.description.trim()) {
    //   newErrors.description = 'Description is required';
    // }

    if (!formData.category) {
      newErrors.category = 'Service category is required';
    }

    if (!formData.priority) {
      newErrors.priority = 'Priority is required';
    }
 

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors below');
      return;
    } 
    setSubmitLoading(true);
    try {
      // Combine regular attachments with pasted images
      const allAttachments = [
        ...attachments,
        ...pastedImages.map(img => img.file)
      ];

      // Use payload mapper for workflow tickets, basic payload for regular tickets
      let ticketData;
      if (['staff_movement', 'new_user', 'access_modification', 'access_termination', 'password_reset', 'change_request'].includes(formData.type)) {
        // Use the payload mapper for workflow tickets
        ticketData = buildTicketPayload(formData.type, {
          ...formData,
          title: formData.subject,
          // Map comment to description for Staff Movement
          description: formData.comment || formData.description,
          declaration: declarationAccepted
        });
        // Add attachments to the mapped payload
        ticketData.attachments = allAttachments;
      } else {
        // Basic payload for regular tickets
        ticketData = {
        title: formData.subject, 
        description: formData.description,
        priority: formData.priority,
        category: formData.category,
        system_id: formData.system_id || '',
        type: formData.type || 'support',
        supplier_user: null,
        attachments: allAttachments
      }; 
      } 

      const response = await createTicket(ticketData); 
      
      if (response.success || response.status === 1) {
        toast.success('Ticket created successfully!');
        onTicketCreated?.(response.data); // Pass the complete response data which now includes 'ticket'
        onClose();
        resetForm();
      } else {
        console.error('Ticket creation failed:', response);
        toast.error(response.message || 'Failed to create ticket');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Error creating ticket. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      subject: '',
      description: '',
      priority: 'medium',
      category: '',
      system_id: '',
      type: '' // Reset type field
    });
    setAttachments([]);
    setPastedImages([]);
    setCurrentStep(1);
    setErrors({});
    setEdamsBilling(false);
    setDeclarationAccepted(false);
  };

  const nextStep = () => {
    const validationErrors = {};
    
    if (currentStep === 1) {
      if (!formData.type) validationErrors.type = 'Ticket type is required';
    }
    
    if (currentStep === 2) {
      if (!formData.subject.trim()) validationErrors.subject = 'Subject is required';
      if (!formData.priority) validationErrors.priority = 'Priority is required';
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setErrors({});
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const getSelectedType = () => {
    return ticketTypes.find(type => type.value === formData.type);
  };

  const getPriorityInfo = () => {
    return priorities.find(p => p.value === formData.priority);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden">
        
        {/* Header - Complete Design */}
        <div className="compact-header text-white px-4 py-3" style={{ background: 'var(--gradient-primary)' }}>
          <div className="flex items-center justify-between">
            {/* Left: Icon + Title */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold leading-tight">Create Support Ticket</h2>
                <p className="text-white/85 text-xs leading-tight">
                  Step {currentStep} of 3 • Get help from our IT team
                </p>
              </div>
            </div>

            {/* Center: Progress Indicator */}
            <div className="flex items-center space-x-2">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                    currentStep === step 
                      ? 'text-white border-2 border-white scale-110' 
                      : currentStep > step
                      ? 'text-green-400 border border-green-400'
                      : 'text-white/60 border border-white/30'
                  }`}>
                    {currentStep > step ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      step
                    )}
                  </div>
                  {step < 3 && (
                    <div className={`w-6 h-0.5 mx-1.5 transition-colors ${
                      currentStep > step ? 'bg-white/70' : 'bg-white/20'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>

            {/* Right: Close Button */}
            <button
              onClick={onClose}
              className="w-8 h-8 hover:bg-white/20 rounded-lg transition-all duration-200 flex items-center justify-center group"
              title="Close"
            >
              <X className="w-5 h-5 group-hover:scale-110 group-hover:rotate-90 transition-all duration-200" />
            </button>
          </div>

          {/* Step Labels */}
          <div className="mt-3 flex items-center justify-center">
            <div className="flex items-center space-x-6 text-xs">
              <div className={`flex items-center space-x-1.5 transition-colors ${
                currentStep === 1 ? 'text-white font-medium' : currentStep > 1 ? 'text-white/70' : 'text-white/50'
              }`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                <span>Ticket Type</span>
              </div>
              <ArrowRight className="w-3 h-3 text-white/40" />
              <div className={`flex items-center space-x-1.5 transition-colors ${
                currentStep === 2 ? 'text-white font-medium' : currentStep > 2 ? 'text-white/70' : 'text-white/50'
              }`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                <span>Priority & Details</span>
              </div>
              <ArrowRight className="w-3 h-3 text-white/40" />
              <div className={`flex items-center space-x-1.5 transition-colors ${
                currentStep === 3 ? 'text-white font-medium' : currentStep > 3 ? 'text-white/70' : 'text-white/50'
              }`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                <span>Description & Submit</span>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading form data...</p>
            </div>
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[calc(95vh-160px)]">
            <form onSubmit={handleSubmit} className="p-4">
              
              {/* Step 1: Ticket Type - Compact */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">What type of ticket are you creating?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {ticketTypes.map((type) => {
                        const Icon = type.icon;
                        const isSelected = formData.type === type.value;
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => handleTypeSelect(type.value)}
                            className={`p-3 rounded-lg border-2 text-left transition-all hover:shadow-md ${
                              isSelected 
                                ? `${type.color} border-current shadow-md` 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start space-x-2">
                              <Icon className={`w-5 h-5 mt-0.5 ${
                                isSelected ? 'text-current' : 'text-gray-400'
                              }`} />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className={`font-medium text-sm ${
                                    isSelected ? 'text-current' : 'text-gray-900'
                                  }`}>
                                    {type.label}
                                  </h4>
                                  {type.isWorkflow && (
                                    <div className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                                      <Workflow className="w-3 h-3" />
                                      <span>Workflow</span>
                                    </div>
                                  )}
                                  {type.requiredPermission && (
                                    <div className={`inline-flex items-center gap-1 text-xs font-medium ${
                                      isSelected ? 'text-current' : 'text-amber-800'
                                    }`}>
                                      <Shield className="w-4 h-4" /> 
                                    </div>
                                  )}
                                </div>
                                <p className={`text-xs mt-0.5 ${
                                  isSelected ? 'text-current opacity-80' : 'text-gray-500'
                                }`}>
                                  {type.description}
                                </p>
                              </div>
                              {isSelected && (
                                <Check className="w-4 h-4 text-current flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {errors.type && (
                      <p className="text-red-600 text-xs mt-2">{errors.type}</p>
                    )}
                  </div>
                              </div>
                              )}

              {/* Step 2: Priority & Details */}
              {currentStep === 2 && (
                    <div className="space-y-4">
                      {/* Display Selected Ticket Type */}
                      {formData.type && (() => {
                        const selectedType = allTicketTypes.find(type => type.value === formData.type);
                        if (selectedType) {
                          const Icon = selectedType.icon;
                          return (
                            <div className={`p-4 rounded-lg border-2 ${selectedType.color} border-current`}>
                              <div className="flex items-start space-x-3">
                                <Icon className="w-6 h-6 text-current flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h3 className="font-semibold text-current">
                                      {selectedType.label}
                                    </h3>
                                    {selectedType.isWorkflow && (
                                      <div className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded bg-blue-100 text-blue-800">
                                        <Workflow className="w-3 h-3" />
                                        <span>Workflow</span>
                                      </div>
                                    )}
                                  </div>
                                  <p className="text-current opacity-80 text-sm">
                                    {selectedType.description}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject
                        </label>
                        <input
                          type="text"
                          name="subject"
                          value={String(formData.subject || '')}
                          onChange={handleInputChange}
                      placeholder="Brief description of your issue"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900 ${
                            errors.subject ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {errors.subject && (
                          <p className="text-red-600 text-xs mt-1">{errors.subject}</p>
                        )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority
                      </label>
                      <select
                        name="priority"
                        value={String(formData.priority || '')}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900"
                      >
                        {priorities.map((priority) => (
                          <option key={priority.value} value={priority.value}>
                            {priority.label}
                          </option>
                        ))}
                      </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Service Category
                        </label>
                        <select
                          name="category"
                          value={String(formData.category || '')}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900"
                        >
                          <option value="">Select a category...</option>
                          {categories.map((category) => (
                            <option key={category.category_id || category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      {errors.category && (
                        <p className="text-red-600 text-xs mt-1">{errors.category}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Detailed Information - Compact */}
              {currentStep === 3 && (
                <div className="space-y-4">

                  {!(['new_user','staff_movement','access_modification','access_termination'].includes(formData.type)) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Related System
                    </label>
                    <SearchableSelect
                      key={`system-${formData.system_id || 'none'}`} // Force re-render when form data changes
                      items={[
                        { value: '', label: 'None / Not applicable' },
                        ...systems.map((system) => ({
                          value: system.asset_id,
                          label: system.asset_name || `Asset ${system.asset_id}`
                        }))
                      ]}
                      onSelectItem={(selectedItem) => {
                        setFormData(prev => ({ 
                          ...prev, 
                          system_id: selectedItem ? selectedItem.value : '' 
                        }));
                      }}
                      initialSelectedItem={(() => {
                        if (formData.system_id) {
                          const selectedSystem = systems.find(s => s.asset_id === formData.system_id);
                          return {
                            value: formData.system_id,
                            label: selectedSystem?.asset_name || `Asset ${formData.system_id}`
                          };
                        }
                        return { value: '', label: 'None / Not applicable' };
                      })()}
                      placeholder="Select a related system..."
                    />
                  </div>
                  )}

                  {/* Workflow-specific fields when applicable */}
                  {formData.type === 'staff_movement' && (
                    <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <label className="block text-sm font-semibold text-gray-800 mb-3">Staff Movement</label>
                      <div className="mb-4">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Select Staff Member</label>
                        {people.length > 0 ? (
                          <SearchableSelect
                            items={[{ value: '', label: 'Select' }, ...people]}
                            onSelectItem={(item) => {
                              if (!item) return;
                              setFormData(prev => ({
                                ...prev,
                                staff_id: item.value,
                                first_name: (item.label || '').split(' ')[0] || prev.first_name,
                                last_name: (item.label || '').split(' ').slice(1).join(' ') || prev.last_name,
                                employee_number: (item.label || '').includes(' - ') ? (item.label || '').split(' - ')[1] : prev.employee_number,
                                current_department: item.department || prev.current_department,
                                current_position: item.position || prev.current_position,
                                // Auto-populate Division and Assigned Post based on selected user
                                department: item.department_id || item.department || prev.department,
                                assigned_post: item.role_id || item.role || prev.assigned_post
                              }));
                            }}
                            placeholder="Select Staff Member"
                          />
                        ) : (
                          <input
                            type="text"
                            className="w-full px-3 py-2 border rounded"
                            placeholder="Type staff full name"
                            value={`${formData.first_name || ''} ${formData.last_name || ''}`.trim()}
                            onChange={(e) => {
                              const val = e.target.value || '';
                              const parts = val.trim().split(' ');
                              setFormData(prev => ({
                                ...prev,
                                first_name: parts[0] || '',
                                last_name: parts.slice(1).join(' ') || ''
                              }));
                            }}
                          />
                        )}
                      </div>

                      {/* Show remaining fields only after staff member is selected */}
                      {formData.staff_id && (
                      <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Division</label>
                          <select
                            className="w-full px-3 py-2 border rounded"
                            value={formData.department || ''}
                            onChange={async (e) => {
                              const dept = e.target.value;
                              setFormData(prev => ({ ...prev, department: dept }));
                              // Note: Department users are loaded based on current user's department,
                              // not the selected department in the form
                            }}
                          >
                            <option value="">Select</option>
                            {departments.map(d => (
                              <option key={d.department_id || d.value} value={d.department_id || d.value}>
                                {d.department_name || d.label || d.name}
                              </option>
                            ))}
                          </select>
                    </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Assigned Post</label>
                          <select
                            className="w-full px-3 py-2 border rounded"
                            value={formData.assigned_post || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, assigned_post: e.target.value }))}
                          >
                            <option value="">Select</option>
                            {roles.map(r => (
                              <option key={r.role_id || r.value} value={r.role_id || r.value}>
                                {r.role_name || r.label || r.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Employee Number</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border rounded"
                            value={formData.employee_number || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, employee_number: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Required by</label>
                          <DatePicker
                            value={formData.required_by || ''}
                            onChange={(val) => setFormData(prev => ({ ...prev, required_by: val }))}
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="border-t border-gray-200 pt-4">
                          <label className="block text-sm font-semibold text-gray-800 mb-2">Requirements</label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-800">
                            <label className="inline-flex items-center space-x-2">
                              <input type="checkbox" checked={!!formData.software_computer} onChange={(e) => setFormData(prev => ({ ...prev, software_computer: e.target.checked }))} />
                              <span>Computer</span>
                            </label>
                            <label className="inline-flex items-center space-x-2">
                              <input type="checkbox" checked={!!formData.software_edams_billing || edamsBilling} onChange={(e) => { setEdamsBilling(e.target.checked); setFormData(prev => ({ ...prev, software_edams_billing: e.target.checked })); }} />
                              <span>Edams Billing</span>
                            </label>
                            <label className="inline-flex items-center space-x-2">
                              <input type="checkbox" checked={!!formData.software_edams_operations} onChange={(e) => setFormData(prev => ({ ...prev, software_edams_operations: e.target.checked }))} />
                              <span>Edams Operations & Maintenance</span>
                            </label>
                            <label className="inline-flex items-center space-x-2">
                              <input type="checkbox" checked={!!formData.software_asset_management} onChange={(e) => setFormData(prev => ({ ...prev, software_asset_management: e.target.checked }))} />
                              <span>Edams Asset Management</span>
                            </label>
                            <label className="inline-flex items-center space-x-2">
                              <input type="checkbox" checked={!!formData.software_office} onChange={(e) => setFormData(prev => ({ ...prev, software_office: e.target.checked }))} />
                              <span>Microsoft Office</span>
                            </label>
                            <label className="inline-flex items-center space-x-2">
                              <input type="checkbox" checked={!!formData.software_imqs} onChange={(e) => setFormData(prev => ({ ...prev, software_imqs: e.target.checked }))} />
                              <span>IMQS</span>
                            </label>
                            <div className="col-span-1 md:col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Other</label>
                              <input type="text" className="w-full px-3 py-2 border rounded text-gray-900" value={formData.software_other || ''} onChange={(e) => setFormData(prev => ({ ...prev, software_other: e.target.value }))} />
                            </div>
                          </div>
                        </div>

                        {edamsBilling && (
                          <div className="mt-4 p-3 rounded-md bg-white border">
                            <div className="mb-2 text-sm font-semibold text-gray-800">EDAMS Access Level Modification</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Application Type</label>
                                <select className="w-full px-3 py-2 border rounded" value={formData.edams_modification || 'modification'} onChange={(e) => setFormData(prev => ({ ...prev, edams_modification: e.target.value }))}>
                                  <option value="new_user">New User</option>
                                  <option value="modification">Modification</option>
                                  <option value="termination">Termination</option>
                                </select>
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Comment</label>
                                <textarea rows={4} className="w-full px-3 py-2 border rounded text-gray-900" value={formData.comment || ''} onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))} />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Declaration field - always visible for workflow tickets */}
                        <div className="mt-4 flex items-start space-x-2 text-sm">
                          <input id="declare-workflow" type="checkbox" checked={declarationAccepted} onChange={(e) => setDeclarationAccepted(e.target.checked)} className="mt-1" />
                          <label htmlFor="declare-workflow">I <strong>{user?.user_full_name || 'User'}</strong>, declare that the information above is correct to the best of my knowledge.</label>
                        </div>
                      </div>
                      </>
                      )}
                    </div>
                  )}

                  {formData.type === 'new_user' && (
                    <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <label className="block text-sm font-semibold text-gray-800 mb-3">New User Setup</label>
                      
                      {/* Personal Information */}
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Personal Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                            <select className="w-full px-3 py-2 border rounded" value={formData.title || ''} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}>
                              <option value="">Select</option>
                              <option value="Mr">Mr</option>
                              <option value="Ms">Ms</option>
                              <option value="Mrs">Mrs</option>
                              <option value="Dr">Dr</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                            <input type="text" className="w-full px-3 py-2 border rounded text-gray-900" value={formData.last_name || ''} onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))} required />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">First Name(s)</label>
                            <input type="text" className="w-full px-3 py-2 border rounded text-gray-900" value={formData.first_name || ''} onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))} required />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Date of Birth</label>
                            <DatePicker
                              value={formData.date_birth || ''}
                              onChange={(val) => setFormData(prev => ({ ...prev, date_birth: val }))}
                              placeholder="Select date"
                              yearRange={{ start: 1900, end: new Date().getFullYear() }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Employment Information */}
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Employment Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Division</label>
                            <select
                              className="w-full px-3 py-2 border rounded"
                              value={formData.division || ''}
                              onChange={async (e) => {
                                const div = e.target.value;
                                setFormData(prev => ({ ...prev, division: div }));
                              }}
                            >
                              <option value="">Select</option>
                              {departments.map(d => (
                                <option key={d.department_id || d.value} value={d.department_id || d.value}>
                                  {d.department_name || d.label}
                                </option>
                              ))}
                            </select>
                              </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Department</label>
                            <select className="w-full px-3 py-2 border rounded" value={formData.department || ''} onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))} required>
                              <option value="">Select</option>
                              {divisionDepartments.map(d => (
                                <option key={d.department_id || d.value} value={d.department_id || d.value}>
                                  {d.department_name || d.label}
                                </option>
                              ))}
                            </select>
                            </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Assigned Post</label>
                            <select className="w-full px-3 py-2 border rounded" value={formData.post || ''} onChange={(e) => setFormData(prev => ({ ...prev, post: e.target.value }))} required>
                              <option value="">Select</option>
                              {roles.map(r => (
                                <option key={r.role_id || r.value} value={r.role_id || r.value}>
                                  {r.role_name || r.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Employee Number</label>
                            <input type="text" className="w-full px-3 py-2 border rounded text-gray-900" value={formData.employee || ''} onChange={(e) => setFormData(prev => ({ ...prev, employee: e.target.value }))} placeholder="If available" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Required by</label>
                            <DatePicker
                              value={formData.required_by || ''}
                              onChange={(val) => setFormData(prev => ({ ...prev, required_by: val }))}
                              placeholder="Select date"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Requirements */}
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Requirements</h4>
                        <div className="space-y-2">
                          <label className="flex items-center space-x-2">
                            <input type="checkbox" checked={formData.software_computer || false} onChange={(e) => setFormData(prev => ({ ...prev, software_computer: e.target.checked }))} />
                            <span className="text-sm">Computer</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input type="checkbox" checked={formData.software_edams_billing || false} onChange={(e) => setFormData(prev => ({ ...prev, software_edams_billing: e.target.checked }))} />
                            <span className="text-sm">Edams Billing</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input type="checkbox" checked={formData.software_edams_operations || false} onChange={(e) => setFormData(prev => ({ ...prev, software_edams_operations: e.target.checked }))} />
                            <span className="text-sm">Edams Operations & Maintenance</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input type="checkbox" checked={formData.software_asset_management || false} onChange={(e) => setFormData(prev => ({ ...prev, software_asset_management: e.target.checked }))} />
                            <span className="text-sm">Edams Asset Management</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input type="checkbox" checked={formData.software_office || false} onChange={(e) => setFormData(prev => ({ ...prev, software_office: e.target.checked }))} />
                            <span className="text-sm">Microsoft Office</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input type="checkbox" checked={formData.software_imqs || false} onChange={(e) => setFormData(prev => ({ ...prev, software_imqs: e.target.checked }))} />
                            <span className="text-sm">IMQS</span>
                          </label>
                        </div>
                      <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Other</label>
                          <input type="text" className="w-full px-3 py-2 border rounded text-gray-900" value={formData.software_other || ''} onChange={(e) => setFormData(prev => ({ ...prev, software_other: e.target.value }))} />
                        </div>
                      </div>

                      {/* EDAMS Access Level Modification - shown when Edams Billing is checked */}
                      {formData.software_edams_billing && (
                        <div className="mt-4 p-3 rounded-md bg-white border">
                          <div className="mb-2 text-sm font-semibold text-gray-800">EDAMS Access Level Modification</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Application Type</label>
                              <select className="w-full px-3 py-2 border rounded" value={formData.edams_modification || 'new_user'} onChange={(e) => setFormData(prev => ({ ...prev, edams_modification: e.target.value }))}>
                                <option value="new_user">New User</option>
                                <option value="modification">Modification</option>
                                <option value="termination">Termination</option>
                              </select>
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Comment</label>
                              <textarea rows={4} className="w-full px-3 py-2 border rounded text-gray-900" value={formData.comment || ''} onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))} />
                            </div>
                        </div>
                      </div>
                    )}

          {/* Declaration field - always visible for workflow tickets */}
          <div className="mt-4 flex items-start space-x-2 text-sm">
            <input id="declare-new-user" type="checkbox" checked={declarationAccepted} onChange={(e) => setDeclarationAccepted(e.target.checked)} className="mt-1" />
            <label htmlFor="declare-new-user">I <strong>{user?.user_full_name || 'User'}</strong>, declare that the information above is correct to the best of my knowledge.</label>
                  </div>
        </div>
      )}

      {/* Access Termination Form */}
      {formData.type === 'access_termination' && (
        <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <label className="block text-sm font-semibold text-gray-800 mb-3">Access Termination</label>
          
          {/* User Selection - ALWAYS REQUIRED for access termination */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Select User <span className="text-red-500">*</span>
            </label>
            {people.length > 0 ? (
              <SearchableSelect
                items={[{ value: '', label: 'Select User' }, ...people]}
                onSelectItem={(item) => {
                  if (!item || !item.value) return;
                  setFormData(prev => ({
                    ...prev,
                    staff_id: item.value,
                    first_name: (item.label || '').split(' ')[0] || '',
                    last_name: (item.label || '').split(' ').slice(1).join(' ') || '',
                    department: item.department || '',
                    current_post: item.position || ''
                  }));
                }}
                placeholder="Select User"
              />
            ) : (
              <input
                type="text"
                className="w-full px-3 py-2 border rounded"
                placeholder="Loading users..."
                disabled
              />
            )}
            <p className="text-xs text-gray-500 mt-1">Please select the user whose access needs to be terminated</p>
          </div>

          {/* Show remaining fields only after user is selected */}
          {formData.staff_id && (
          <>
          {/* Personal Information - All fields read-only after user selection */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Personal Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                <select 
                  className="w-full px-3 py-2 border rounded bg-gray-100 cursor-not-allowed" 
                  value={formData.title || ''} 
                  disabled
                >
                  <option value="">Select</option>
                  <option value="Mr">Mr</option>
                  <option value="Ms">Ms</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Dr">Dr</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                <input 
                  type="text" 
                  className="w-full px-3 py-2 border rounded bg-gray-100 cursor-not-allowed" 
                  value={formData.last_name || ''} 
                  readOnly
                  required 
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">First Name(s)</label>
                <input 
                  type="text" 
                  className="w-full px-3 py-2 border rounded bg-gray-100 cursor-not-allowed" 
                  value={formData.first_name || ''} 
                  readOnly
                  required 
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Division</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded bg-gray-100 cursor-not-allowed"
                  value={departments.find(d => (d.department_id || d.value) == formData.department)?.department_name || departments.find(d => (d.department_id || d.value) == formData.department)?.label || ''}
                  readOnly
                />
              </div>
              {formData.current_post && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Current Post</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded bg-gray-100 cursor-not-allowed"
                    value={formData.current_post || ''}
                    readOnly
                  />
                </div>
              )}
            </div>
          </div>

          {/* Termination Information */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Termination Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Application Type</label>
                <select className="w-full px-3 py-2 border rounded bg-gray-100" value="termination" readOnly>
                  <option value="termination">Termination</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                <select className="w-full px-3 py-2 border rounded" value={formData.priority || 'medium'} onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
          </div>

          {/* Comment */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">Comment (Optional)</label>
            <textarea rows={4} className="w-full px-3 py-2 border rounded text-gray-900" value={formData.comment || ''} onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))} placeholder="Add any additional notes or reasons for termination..." />
          </div>

          {/* Declaration field - always visible for workflow tickets */}
          <div className="mt-4 flex items-start space-x-2 text-sm">
            <input id="declare-termination" type="checkbox" checked={declarationAccepted} onChange={(e) => setDeclarationAccepted(e.target.checked)} className="mt-1" />
            <label htmlFor="declare-termination">I <strong>{user?.user_full_name || 'User'}</strong>, declare that the information above is correct to the best of my knowledge.</label>
          </div>
          </>
          )}
        </div>
      )}

      {/* Access Modification Form */}
      {formData.type === 'access_modification' && (
        <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <label className="block text-sm font-semibold text-gray-800 mb-3">Access Modification</label>
          
          {/* User Selection */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">Select User *</label>
            {people.length > 0 ? (
              <SearchableSelect
                items={[{ value: '', label: 'Select User' }, ...people]}
                onSelectItem={(item) => {
                  if (!item || !item.value) return;
                  setFormData(prev => ({
                    ...prev,
                    staff_id: item.value,
                    first_name: (item.label || '').split(' ')[0] || '',
                    last_name: (item.label || '').split(' ').slice(1).join(' ') || '',
                    department: item.department_id || item.department || '',
                    current_post: item.role_id || item.position || ''
                  }));
                }}
                placeholder="Select User"
              />
            ) : (
              <input
                type="text"
                className="w-full px-3 py-2 border rounded bg-gray-100"
                placeholder="Loading users..."
                disabled
              />
            )}
          </div>

          {/* Show remaining fields only after user is selected */}
          {formData.staff_id && (
          <>
          {/* Personal Information */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Personal Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Last Name *</label>
                <input 
                  type="text" 
                  className="w-full px-3 py-2 border rounded bg-gray-100" 
                  value={formData.last_name || ''} 
                  readOnly 
                  required 
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">First Name(s) *</label>
                <input 
                  type="text" 
                  className="w-full px-3 py-2 border rounded bg-gray-100" 
                  value={formData.first_name || ''} 
                  readOnly 
                  required 
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Current Post *</label>
                <select
                  className="w-full px-3 py-2 border rounded bg-gray-100"
                  value={formData.current_post || ''}
                  disabled
                >
                  <option value="">Select</option>
                  {roles.map(r => (
                    <option key={r.role_id || r.value} value={r.role_id || r.value}>
                      {r.role_name || r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Department *</label>
                <select
                  className="w-full px-3 py-2 border rounded bg-gray-100"
                  value={formData.department || ''}
                  disabled
                >
                  <option value="">Select</option>
                  {departments.map(d => (
                    <option key={d.department_id || d.value} value={d.department_id || d.value}>
                      {d.department_name || d.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Position Information */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Position Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Current Post</label>
                <select className="w-full px-3 py-2 border rounded" value={formData.current_post || ''} onChange={(e) => setFormData(prev => ({ ...prev, current_post: e.target.value }))}>
                  <option value="">Select</option>
                  {roles.map(r => (
                    <option key={r.role_id || r.value} value={r.role_id || r.value}>
                      {r.role_name || r.label}
                    </option>
                  ))}
                </select>
                        </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Assigned Post</label>
                <select className="w-full px-3 py-2 border rounded" value={formData.post || ''} onChange={(e) => setFormData(prev => ({ ...prev, post: e.target.value }))}>
                  <option value="">Select</option>
                  {roles.map(r => (
                    <option key={r.role_id || r.value} value={r.role_id || r.value}>
                      {r.role_name || r.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Modification Information */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Modification Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Application Type</label>
                <select className="w-full px-3 py-2 border rounded" value={formData.edams_modification || 'modification'} onChange={(e) => setFormData(prev => ({ ...prev, edams_modification: e.target.value }))}>
                  <option value="new_user">New User</option>
                  <option value="modification">Modification</option>
                  <option value="termination">Termination</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                <select className="w-full px-3 py-2 border rounded" value={formData.priority || 'medium'} onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
          </div>

          {/* Additional Fields */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Additional Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Limit</label>
                <select className="w-full px-3 py-2 border rounded" value={formData.limit || 'No change required'} onChange={(e) => setFormData(prev => ({ ...prev, limit: e.target.value }))}>
                  <option value="No change required">No change required</option>
                  <option value="Increase">Increase</option>
                  <option value="Decrease">Decrease</option>
                </select>
              </div>
              {(formData.limit === 'Increase' || formData.limit === 'Decrease') && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">New Limit</label>
                  <input type="text" className="w-full px-3 py-2 border rounded text-gray-900" value={formData.new_limit || ''} onChange={(e) => setFormData(prev => ({ ...prev, new_limit: e.target.value }))} />
                      </div>
                    )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
                <DatePicker
                  value={formData.from_date || ''}
                  onChange={(val) => setFormData(prev => ({ ...prev, from_date: val }))}
                  placeholder="Select date"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
                <DatePicker
                  value={formData.to_date || ''}
                  onChange={(val) => setFormData(prev => ({ ...prev, to_date: val }))}
                  placeholder="Select date"
                />
              </div>
            </div>
                  </div>
  
          {/* Comment */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">Comment</label>
            <textarea rows={4} className="w-full px-3 py-2 border rounded text-gray-900" value={formData.comment || ''} onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))} />
          </div>

          {/* Declaration field - always visible for workflow tickets */}
          <div className="mt-4 flex items-start space-x-2 text-sm">
            <input id="declare-modification" type="checkbox" checked={declarationAccepted} onChange={(e) => setDeclarationAccepted(e.target.checked)} className="mt-1" />
            <label htmlFor="declare-modification">I <strong>{user?.user_full_name || 'User'}</strong>, declare that the information above is correct to the best of my knowledge.</label>
          </div>
          </>
          )}
        </div>
      )}

      {/* Password Reset Form */}
      {formData.type === 'password_reset' && (
        <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <label className="block text-sm font-semibold text-gray-800 mb-3">Password Reset Request</label>
          
          {/* Supplier Information */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Supplier Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Supplier Name</label>
                <input type="text" className="w-full px-3 py-2 border rounded text-gray-900" value={formData.supplier_name || ''} onChange={(e) => setFormData(prev => ({ ...prev, supplier_name: e.target.value }))} required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Supplier Email</label>
                <input type="email" className="w-full px-3 py-2 border rounded text-gray-900" value={formData.supplier_email || ''} onChange={(e) => setFormData(prev => ({ ...prev, supplier_email: e.target.value }))} required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Supplier Contact Person</label>
                <input type="text" className="w-full px-3 py-2 border rounded text-gray-900" value={formData.contact_person || ''} onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Supplier Phone</label>
                <input type="tel" className="w-full px-3 py-2 border rounded text-gray-900" value={formData.supplier_phone || ''} onChange={(e) => setFormData(prev => ({ ...prev, supplier_phone: e.target.value }))} />
              </div>
            </div>
          </div>

          {/* System Information */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">System Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Username/Account ID</label>
                <input type="text" className="w-full px-3 py-2 border rounded text-gray-900" value={formData.username || ''} onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))} required />
              </div>
            </div>
          </div>

          {/* Reset Details */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Reset Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reset Reason</label>
                <select className="w-full px-3 py-2 border rounded" value={formData.reset_reason || ''} onChange={(e) => setFormData(prev => ({ ...prev, reset_reason: e.target.value }))} required>
                  <option value="">Select Reason</option>
                  <option value="forgot_password">Forgot Password</option>
                  <option value="account_locked">Account Locked</option>
                  <option value="password_expired">Password Expired</option>
                  <option value="security_breach">Security Breach</option>
                  <option value="new_account">New Account Setup</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                <select className="w-full px-3 py-2 border rounded" value={formData.priority || 'medium'} onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Additional Information</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Business Justification</label>
                <textarea rows={3} className="w-full px-3 py-2 border rounded text-gray-900" value={formData.business_justification || ''} onChange={(e) => setFormData(prev => ({ ...prev, business_justification: e.target.value }))} placeholder="Explain why this password reset is needed..." />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Expected Resolution Time</label>
                <select className="w-full px-3 py-2 border rounded" value={formData.expected_resolution || ''} onChange={(e) => setFormData(prev => ({ ...prev, expected_resolution: e.target.value }))}>
                  <option value="">Select Timeframe</option>
                  <option value="immediate">Immediate (within 1 hour)</option>
                  <option value="same_day">Same Day</option>
                  <option value="next_business_day">Next Business Day</option>
                  <option value="within_week">Within 1 Week</option>
                </select>
              </div>
            </div>
          </div>

          {/* Declaration field - always visible for workflow tickets */}
          <div className="mt-4 flex items-start space-x-2 text-sm">
            <input id="declare-password-reset" type="checkbox" checked={declarationAccepted} onChange={(e) => setDeclarationAccepted(e.target.checked)} className="mt-1" />
            <label htmlFor="declare-password-reset">I <strong>{user?.user_full_name || 'User'}</strong>, declare that the information above is correct to the best of my knowledge.</label>
          </div>
        </div>
      )}

      {/* General Form for Non-Workflow Ticket Types */}
      {!['new_user', 'staff_movement', 'access_modification', 'access_termination', 'password_reset'].includes(formData.type) && (
        <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <label className="block text-sm font-semibold text-gray-800 mb-3">Ticket Details</label>
          
          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Detailed Description
            </label>
            <textarea
              name="description"
              value={String(formData.description || '')}
              onChange={handleInputChange}
              onPaste={handlePaste}
              rows={4}
              placeholder="Please provide a detailed description of your issue or request..."
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900 ${
                errors.description ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            <p className="text-xs text-gray-500 mt-1">
              💡 Tip: You can paste screenshots directly using Ctrl+V (or Cmd+V on Mac)
            </p>
            {errors.description && (
              <p className="text-red-600 text-xs mt-1">{errors.description}</p>
            )}
            
            {/* Pasted Images Preview */}
            {pastedImages.length > 0 && (
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Pasted Screenshots ({pastedImages.length})
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {pastedImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image.preview}
                        alt={image.name}
                        className="w-full h-24 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove image"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <p className="text-xs text-gray-600 mt-1 truncate">{image.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments
                    </label>
                    <MultiFileInput
                      files={attachments}
                      onChange={handleAttachmentsChange}
                      multiple={true}
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt,.zip,.xlsx,.pptx"
                      maxSize={10 * 1024 * 1024} // 10MB
                      className="w-full"
                    /> 
                  </div>
 
                </div>
              )}

              {/* Navigation and Actions - Hidden on Step 1 */}
              {currentStep > 1 && (
              <div className="flex items-center justify-between pt-8 border-t border-gray-200 mt-8">
                <div className="flex space-x-3">
                  {currentStep > 1 && (
                    <div
                      onClick={prevStep}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer transition-colors"
                    >
                      Previous
                    </div>
                  )}
                </div>

                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  
                  {currentStep < 3 ? (
                    <div
                      onClick={nextStep}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer transition-colors"
                    >
                      <span>Next</span>
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </div>
                  ) : (
                    <Button
                      type="submit"
                      disabled={submitLoading || (['new_user','staff_movement'].includes(formData.type) && formData.software_edams_billing && !declarationAccepted)}
                      {...(submitLoading && { loading: true })}
                      icon={Send}
                    >
                      {submitLoading ? 'Creating Ticket...' : 'Create Ticket'}
                    </Button>
                  )}
                </div>
              </div>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewTicketModal;
